/* eslint-disable array-element-newline */
import { TWasmBaseMaps } from "~/interfaces";
import { decOpacityToHex, getGeometryStyleHash } from "./utils";
import { TWasmTextureNames, WASM_ALL_TEXTURE_NAMES } from "./textures-data";
import { IDefPosition, TLangs } from "./interfaces";

// pri nove verzi se samozapisuje, prosim nemenit
export const WASM_VERSION = {
	version: "1.6.9",
	date: "2025-11-14",
};

export const LAYER_MAPPING: {[key in TWasmBaseMaps]: string } = {
	"zakladni": "web_a_base",
	"turisticka": "web_b_tourist",
	"zimni": "web_c_winter",
	"letecka": "web_d_ophoto",
	"dopravni": "web_e_traffic",
	"zemepisna": "web_f_geography",
	"19stoleti": "web_g_19century",
};

// prevzato ze stylu xml
export const LAYER_MAPPING_NUMB: {[key in TWasmBaseMaps]: number } = {
	"zakladni": 1,
	"turisticka": 3,
	"zimni": 7,
	"letecka": 275,
	"dopravni": 21,
	"zemepisna": 30,
	"19stoleti": 6,
};

export const OPHOTO_YEAR_SRC_MAPPING: Record<string, string> = {
	/* eslint-disable quote-props */
	"2021": "ophoto1921",
	"2018": "ophoto1618-m",
	"2015": "ophoto1415-m",
	"2012": "ophoto1012-m",
	"2006": "ophoto0406-m",
	"2003": "ophoto0203-m",
	/* eslint-enable quote-props */
};

export const TEXTURES_NAMES = {
	main: "main-texture",
	custom: "custom-texture",
};

export const WASM_TEXTURES = {
	defSize: 100,
	pathCaption: 100,
	textLineHeight: 15,
	searchMarkersWidth: 125,
	maxReview: 5,
};

export const DEF_POPUP_ARROW_ANGLES = {
	TOP_LEFT: 315,
	TOP_RIGHT: 45,
	BOTTOM_RIGHT: 135,
	BOTTOM_LEFT: 225,
};
export const ALL_POPUP_ANGLES = [DEF_POPUP_ARROW_ANGLES.BOTTOM_LEFT, DEF_POPUP_ARROW_ANGLES.BOTTOM_RIGHT, DEF_POPUP_ARROW_ANGLES.TOP_LEFT, DEF_POPUP_ARROW_ANGLES.TOP_RIGHT];

export const DEF_DPIS = [72, 108, 144, 216];

export const TARGET_FPS = {
	mobile: 60,
	chrome: 0,
	firefox: 0,
};

const keepExactWidth = true;

export const DEF_LINE_STYLES = {
	DEFAULT: getGeometryStyleHash({
		color: "#1eae00",
		width: 8,
		keepExactWidth,
		lineCap: "square",
	}),
	MEASURE_LINE_ACTIVE: getGeometryStyleHash({
		color: "#ff0000",
		width: 8,
		keepExactWidth,
		lineCap: "square",
	}),
	MEASURE_LINE_INACTIVE: getGeometryStyleHash({
		color: "#6b7580",
		width: 8,
		keepExactWidth,
		lineCap: "square",
	}),
	MEASURE_CURRENT_ACTIVE: getGeometryStyleHash({
		color: "#ff0000",
		width: 8,
		keepExactWidth,
		dashed: "20",
	}),
	MEASURE_CURRENT_INACTIVE: getGeometryStyleHash({
		color: "#6b7580",
		width: 8,
		keepExactWidth,
		dashed: "20",
	}),
	BACKPACK_PATH_ACTIVE: getGeometryStyleHash({
		// stroke opacity 0.65 -> (0.65*255 >>> 0).toString(16) = a5
		color: `#ff0000${decOpacityToHex(0.65)}`,
		width: 6,
		edgeColor: "#ffffff",
		edgeWidth: 0,
		keepExactWidth,
	}),
	BACKPACK_PATH_INACTIVE: getGeometryStyleHash({
		color: `#6b7580`,
		width: 6,
		edgeColor: "#ffffff",
		edgeWidth: 0,
		keepExactWidth,
	}),
	ACTIVITY_DETAIL: getGeometryStyleHash({
		color: `#ff0000${decOpacityToHex(0.65)}`,
		width: 12,
		keepExactWidth,
	}),
	ACTIVITY_LOOKUP: getGeometryStyleHash({
		color: `#ff0000ff`,
		width: 6,
		keepExactWidth,
	}),
	POI_DETAIL_GEOM: getGeometryStyleHash({
		color: `#ff0000`,
		width: 6,
		edgeColor: "#ffffff",
		edgeWidth: 4,
		keepExactWidth,
	}),
};

const MAX_ZINDEX_NORMAL = 100;
const MAX_ZINDEX_HOVER = 110;
const MAX_ZINDEX_MIN = 10;

// 0 nejnizsi priorita - linie jsou 0-10k, pak jdou markery interne; markery + vrstvy dohromady
export const WASM_ZINDEX = {
	marker: {
		poiDetail: MAX_ZINDEX_NORMAL,
		poiDetailPhotos: MAX_ZINDEX_HOVER,
		poiDetailSigns: [MAX_ZINDEX_NORMAL, MAX_ZINDEX_HOVER],
		geoLocation: MAX_ZINDEX_MIN,
		interactiveGeometry: MAX_ZINDEX_HOVER,
		heightProfile: MAX_ZINDEX_HOVER,
		pano: MAX_ZINDEX_NORMAL,
		activityDetail: MAX_ZINDEX_NORMAL,
		activityLookup: [50, MAX_ZINDEX_NORMAL],
		measure: [MAX_ZINDEX_NORMAL, MAX_ZINDEX_HOVER],
		myReviews: MAX_ZINDEX_NORMAL,
		myReviewsHover: MAX_ZINDEX_HOVER,
		pathDetail: MAX_ZINDEX_NORMAL,
		placesLookupLittle: [MAX_ZINDEX_MIN, MAX_ZINDEX_HOVER],
		placesLookupSmall: [50, MAX_ZINDEX_HOVER],
		placesLookupPathCaption: MAX_ZINDEX_NORMAL,
		routePointCircle: 0,
		routeStart: [95, MAX_ZINDEX_HOVER],
		routeEnd: [MAX_ZINDEX_NORMAL, MAX_ZINDEX_HOVER],
		routeWaypoint: [90, MAX_ZINDEX_HOVER],
		routeTripWaypoint: [90, MAX_ZINDEX_HOVER],
		routePopupInfo: 80,
		routePopupWeather: 80,
		routePopupPubt: 80,
		routePopupClosure: 80,
		alwaysOnMap: MAX_ZINDEX_MIN,
		searchLittle: [50, MAX_ZINDEX_HOVER],
		searchBig: [MAX_ZINDEX_NORMAL - 2, MAX_ZINDEX_HOVER],
		searchSmall: [MAX_ZINDEX_NORMAL - 5, MAX_ZINDEX_HOVER],
		searchHover: MAX_ZINDEX_HOVER,
		userMarks: [MAX_ZINDEX_NORMAL, MAX_ZINDEX_HOVER],
		/** lookup fotky */
		photo: [MAX_ZINDEX_MIN, MAX_ZINDEX_HOVER],
		/** lookup webkamery */
		webcam: [MAX_ZINDEX_MIN, MAX_ZINDEX_HOVER],
		/** sdileni polohy */
		sharePosition: [45, MAX_ZINDEX_HOVER],
		/** dnd u routy */
		routeDnd: MAX_ZINDEX_HOVER,
		/** Soucasne mhd */
		oldPubt: MAX_ZINDEX_HOVER,
	},
	geometry: {
		// route - aktivni 50/75
		routeRoutes: [50, 75],
		// route - dopravni dostupnost 51/76
		routeTraffic: [51, 76],
		poiDetail: MAX_ZINDEX_NORMAL,
		activityDetail: MAX_ZINDEX_NORMAL,
		activityLookup: MAX_ZINDEX_NORMAL,
		measureCurrent: [MAX_ZINDEX_NORMAL, MAX_ZINDEX_HOVER],
		measureLine: [MAX_ZINDEX_NORMAL, MAX_ZINDEX_HOVER],
		pathDetail: MAX_ZINDEX_NORMAL,
		backpackRoute: [MAX_ZINDEX_NORMAL, MAX_ZINDEX_HOVER],
		/** sdileni polohy */
		sharePosition: MAX_ZINDEX_NORMAL,
		/** Soucasne mhd */
		oldPubt: MAX_ZINDEX_HOVER,
	},
};

// @ts-ignore
export const MAIN_TEXTURES: {[key in TWasmTextureNames] : string} = {};
export const AUTO_TEXTURES: Array<TWasmTextureNames> = ["pano", "measure-start-active", "measure-start-inactive", "measure-points-active", "measure-points-inactive", "height-profile", "always-on-map", "little-active", "little-inactive",
/* eslint-disable-next-line */
	"small-universal-active", "small-universal-inactive", "position-share-image-arrow", "big-universal-active", "big-universal-inactive"];

/**
 * Ziskani nazvu textury - custom se handluje pak jinak
 */
export function getTextureName(name: string, isCustom?: boolean) {
	// jednoduse texture:// ignoruje data callback, ostatni tam spadnou, https://...api.mapy.cz
	return `${isCustom ? TEXTURES_NAMES.custom : TEXTURES_NAMES.main}://${name || ""}`;
}

for (const wasmTextureName of WASM_ALL_TEXTURE_NAMES) {
	MAIN_TEXTURES[wasmTextureName] = getTextureName(wasmTextureName);
}

export type TCustomTexturesNames = "geo-location" | "user-marks-active" | "user-marks-inactive" | "user-marks-opacity-active" | "small-active" | "small-inactive" | "route-start-active" | "route-start-inactive" | "route-end-active" | "route-end-inactive" | "route-start-opacity-active"
	| "route-end-opacity-active" | "search-badge-text-active" | "search-badge-text-inactive" | "photo-active" | "photo-inactive" | "webcam-active" | "webcam-inactive" | "position-share-device-active" | "position-share-device-inactive" | "position-share-image-active" | "position-share-image-inactive"
	| "position-share-image-gray-active" | "position-share-image-gray-inactive" | "path-caption" | "route-point-circle" | "small-gray" | "position-share-image-radius" | "route-popup-info-white" | "route-popup-info-blue" | "route-popup-weather-temp" | "route-popup-weather-precp" | "route-popup-weather-wind"
	| "route-popup-pubt-summary" | "route-popup-pubt-section" | "route-popup-closure";

const allCustomTextures: Array<TCustomTexturesNames> = ["geo-location", "user-marks-active", "user-marks-inactive", "user-marks-opacity-active", "small-active", "small-inactive", "route-start-active", "route-start-inactive", "route-end-active", "route-end-inactive", "route-start-opacity-active",
	"route-end-opacity-active", "search-badge-text-active", "search-badge-text-inactive", "photo-active", "photo-inactive", "webcam-active", "webcam-inactive", "position-share-device-active", "position-share-device-inactive", "position-share-image-active", "position-share-image-inactive",
	"position-share-image-gray-active", "position-share-image-gray-inactive", "path-caption", "route-point-circle", "small-gray", "position-share-image-radius", "route-popup-info-white", "route-popup-info-blue", "route-popup-weather-temp", "route-popup-weather-precp", "route-popup-weather-wind",
	"route-popup-pubt-summary", "route-popup-pubt-section", "route-popup-closure"];

// @ts-ignore
export const CUSTOM_TEXTURES: {[key in TCustomTexturesNames]: string} = {};

for (const customTexure of allCustomTextures) {
	CUSTOM_TEXTURES[customTexure] = getTextureName(customTexure, true);
}

export const TEXTURES_CONF = {
	// 0 se renderuje prvni, pak 1, pak 2...
	priorityLayer: 0,
	opacity: 0.7,
	circleWidth: 256,
	circleColorGeoLocation: "rgba(0, 168, 255, 0.2)",
	circleColorPositionShare: "rgba(111, 143, 247, 0.3)",
	routePopupActiveColor: "#0033ff",
	routePopupInactiveColor: "#6b7580",
};

export const DEF_POSITION: IDefPosition = {
	lon: 0,
	lat: 0,
	zoom: 0,
};

export const TILT_DATA = {
	angles: [0, 60],
	distance: 200,
};

export const WIND_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25 24" width="25" height="24">
	<path fill="#000" d="M13.577 6.92c-.172-.172-.366-.263-.566-.269-.222.006-.422.103-.61.292-.172.17-.263.365-.257.576l.005 6.901-1.113-1.113a.821.821 0 0 0-.599-.257.821.821 0 0 0-.6.257.793.793 0 0 0-.245.6.834.834 0 0 0 .257.599l2.552 2.551a.821.821 0 0 0 .599.257.816.816 0 0 0 .594-.25l2.568-2.57a.785.785 0 0 0 .246-.587.834.834 0 0 0-.263-.594.81.81 0 0 0-.582-.251.81.81 0 0 0-.582.25l-1.113 1.114V7.508a.964.964 0 0 0-.291-.588z"></path>
</svg>`;

export const CZECH_TILES: Array<TLangs> = ["cs", "sk"];
